import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Smartphone, Tablet, Monitor, Tv, Glasses, Download, Sparkles, Image as ImageIcon } from "lucide-react";

const PLATFORMS = [
  { id: "ios", label: "iOS", desc: "iPhone — iOS 17+", icon: <Smartphone className="w-5 h-5" /> },
  { id: "ipados", label: "iPadOS", desc: "iPad — iPadOS 17+", icon: <Tablet className="w-5 h-5" /> },
  { id: "macos", label: "macOS", desc: "Mac — macOS 14+", icon: <Monitor className="w-5 h-5" /> },
  { id: "tvos", label: "tvOS", desc: "Apple TV — tvOS 17+", icon: <Tv className="w-5 h-5" /> },
  { id: "visionos", label: "visionOS", desc: "Vision Pro — visionOS 1+", icon: <Glasses className="w-5 h-5" /> },
];

export default function PlatformPicker({ open, onOpenChange, onConfirm, busy, title = "Export starter kit" }) {
  const [selected, setSelected] = useState(["ios"]);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(["ios"]);
      setMarketing(false);
    }
  }, [open]);

  const toggle = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const confirm = () => {
    if (selected.length === 0) return;
    onConfirm(selected, { marketing });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream border-2 border-ink rounded-3xl max-w-lg max-h-[92vh] overflow-y-auto" data-testid="platform-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{title}</DialogTitle>
          <DialogDescription className="text-ink2">
            Pick the Apple platforms to bundle. Your zip will include shared SwiftUI sources, per-platform <code className="font-mono text-xs bg-paper px-1.5 py-0.5 rounded">Info.plist</code>, a FastAPI + MongoDB backend, and an App Store submission README.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 my-3" data-testid="platform-options">
          {PLATFORMS.map(p => {
            const checked = selected.includes(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.id)}
                className={`w-full flex items-center gap-4 p-3.5 rounded-2xl border-2 text-left transition ${
                  checked ? "border-saffron bg-saffron/10" : "border-border bg-white hover:border-ink"
                }`}
                data-testid={`platform-${p.id}`}
              >
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${checked ? "bg-saffron text-ink" : "bg-paper text-ink2"}`}>
                  {p.icon}
                </span>
                <span className="flex-1">
                  <span className="block font-semibold text-ink">{p.label}</span>
                  <span className="block text-xs text-ink2">{p.desc}</span>
                </span>
                <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${checked ? "bg-ink border-ink" : "border-border"}`}>
                  {checked && <span className="text-cream text-xs font-bold">✓</span>}
                </span>
              </button>
            );
          })}
        </div>

        <div className="my-3">
          <button
            type="button"
            onClick={() => setMarketing(m => !m)}
            className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition ${
              marketing ? "border-marigold bg-marigold/10" : "border-dashed border-marigold/40 bg-white hover:bg-marigold/5"
            }`}
            data-testid="marketing-assets-toggle"
          >
            <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${marketing ? "bg-marigold text-cream" : "bg-marigold/15 text-marigold"}`}>
              <ImageIcon className="w-5 h-5" />
            </span>
            <span className="flex-1">
              <span className="font-semibold text-ink inline-flex items-center gap-2">
                App Store marketing screenshots
                <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-saffron text-ink inline-flex items-center gap-1">
                  <Sparkles className="w-2.5 h-2.5" /> Add-on
                </span>
              </span>
              <span className="block text-xs text-ink2 mt-0.5">
                5 AI-generated marketing screenshots (gpt-image-1) saved to <code className="font-mono">Assets.xcassets/Marketing/</code>. Adds ~45–60s.
              </span>
            </span>
            <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${marketing ? "bg-marigold border-marigold" : "border-border"}`}>
              {marketing && <span className="text-cream text-xs font-bold">✓</span>}
            </span>
          </button>
        </div>

        <DialogFooter className="flex flex-row justify-between items-center gap-2">
          <div className="text-xs text-ink2">
            {selected.length} platform{selected.length !== 1 ? "s" : ""}
            {marketing && <span className="ml-2 text-marigold font-semibold">+ marketing</span>}
          </div>
          <button
            onClick={confirm}
            disabled={busy || selected.length === 0}
            className="btn-primary text-sm py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="platform-picker-confirm"
          >
            <Download className="w-4 h-4" />
            {busy ? (marketing ? "Generating screenshots..." : "Building...") : "Download .zip"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
